import { COLORS } from "@coeff/utils";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useCaptureLoadingTime } from "./useCaptureLoadingTime.hook";

interface LoaderProps {
  color?: "white" | "blue";
  cssOnly?: boolean;
  delay?: number;
  size?: "small" | "large" | "micro";
  style?: object;
  title?: string;
  verticalAlign?: boolean;
}

const CSSSpinner = styled.div`
  display: inline-block;
  border: 3px solid ${COLORS.black15};
  border-top-color: ${COLORS.coeblue4};
  border-radius: 50%;
  animation: css-spin 0.8s linear infinite;
  will-change: transform;

  &.large {
    width: 48px;
    height: 48px;
  }

  &.small,
  & {
    width: 24px;
    height: 24px;
  }

  &.micro {
    width: 16px;
    height: 16px;
    border-width: 2px;
  }

  @keyframes css-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoaderContainer = styled.div`
  margin: 2rem auto;
  text-align: center;
`;

const LoaderPanel = styled.div`
  margin: auto;
  text-align: center;

  .loadTitle {
    margin-top: 0.5rem;
    color: ${COLORS.black45};
  }
`;

const LoaderIcon = styled.div`
  display: inline-block;
  margin: auto;
  width: 24px;
  height: 24px;

  &.large {
    width: 48px;
    height: 48px;
  }

  &.micro {
    width: 16px;
    height: 16px;
  }
`;

const StyledBlueLoader = styled.svg`
  height: 100%;

  #e8erxt12eadt10_tr {
    animation: e8erxt12eadt10_tr__tr 2300ms linear infinite normal forwards;
  }
  @keyframes e8erxt12eadt10_tr__tr {
    0% {
      transform: translate(54px, 54px) rotate(-45deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    26.086957% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    39.130435% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    78.260870% {
      transform: translate(54px, 54px) rotate(180deg);
    }
    100% {
      transform: translate(54px, 54px) rotate(180deg);
    }
  }
  #e8erxt12eadt10 {
    animation-name: e8erxt12eadt10_s_o, e8erxt12eadt10_s_do;
    animation-duration: 2300ms;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    animation-direction: normal;
    animation-iteration-count: infinite;
  }
  @keyframes e8erxt12eadt10_s_o {
    0% {
      stroke-opacity: 0;
    }
    8.695652% {
      stroke-opacity: 1;
    }
    69.565217% {
      stroke-opacity: 1;
    }
    78.260870% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 0;
    }
  }
  @keyframes e8erxt12eadt10_s_do {
    0% {
      stroke-dashoffset: 942.48;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    26.086957% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    39.130435% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    78.260870% {
      stroke-dashoffset: 314.16;
    }
    100% {
      stroke-dashoffset: 314.16;
    }
  }
  #e8erxt12eadt11_tr {
    animation: e8erxt12eadt11_tr__tr 2300ms linear infinite normal forwards;
  }
  @keyframes e8erxt12eadt11_tr__tr {
    0% {
      transform: translate(54px, 54px) rotate(-45deg);
    }
    13.043478% {
      transform: translate(54px, 54px) rotate(-45deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    30.434783% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    52.173913% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    82.608696% {
      transform: translate(54px, 54px) rotate(180deg);
    }
    100% {
      transform: translate(54px, 54px) rotate(180deg);
    }
  }
  #e8erxt12eadt11 {
    animation-name: e8erxt12eadt11_s_o, e8erxt12eadt11_s_do;
    animation-duration: 2300ms;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    animation-direction: normal;
    animation-iteration-count: infinite;
  }
  @keyframes e8erxt12eadt11_s_o {
    0% {
      stroke-opacity: 0;
    }
    13.043478% {
      stroke-opacity: 0;
    }
    17.391304% {
      stroke-opacity: 1;
    }
    73.913043% {
      stroke-opacity: 1;
    }
    82.608696% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 0;
    }
  }
  @keyframes e8erxt12eadt11_s_do {
    0% {
      stroke-dashoffset: 942.48;
    }
    17.391304% {
      stroke-dashoffset: 942.48;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    30.434783% {
      stroke-dashoffset: 800;
    }
    47.826087% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    60.869565% {
      stroke-dashoffset: 567.581767;
    }
    82.608696% {
      stroke-dashoffset: 314.16;
    }
    100% {
      stroke-dashoffset: 314.16;
    }
  }
  #e8erxt12eadt12_tr {
    animation: e8erxt12eadt12_tr__tr 2300ms linear infinite normal forwards;
  }
  @keyframes e8erxt12eadt12_tr__tr {
    0% {
      transform: translate(54px, 54px) rotate(-45deg);
    }
    17.391304% {
      transform: translate(54px, 54px) rotate(-45deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    39.130435% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    65.217391% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    86.956522% {
      transform: translate(54px, 54px) rotate(180deg);
    }
    100% {
      transform: translate(54px, 54px) rotate(180deg);
    }
  }
  #e8erxt12eadt12 {
    animation-name: e8erxt12eadt12_s_o, e8erxt12eadt12_s_do;
    animation-duration: 2300ms;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    animation-direction: normal;
    animation-iteration-count: infinite;
  }
  @keyframes e8erxt12eadt12_s_o {
    0% {
      stroke-opacity: 0;
    }
    17.391304% {
      stroke-opacity: 0;
    }
    26.086957% {
      stroke-opacity: 1;
    }
    86.956522% {
      stroke-opacity: 1;
    }
    100% {
      stroke-opacity: 0;
    }
  }
  @keyframes e8erxt12eadt12_s_do {
    0% {
      stroke-dashoffset: 942.48;
    }
    26.086957% {
      stroke-dashoffset: 942.48;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    56.521739% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    100% {
      stroke-dashoffset: 314.16;
    }
  }
  #e8erxt12eadt13_ts {
    animation: e8erxt12eadt13_ts__ts 2300ms linear infinite normal forwards;
  }
  @keyframes e8erxt12eadt13_ts__ts {
    0% {
      transform: translate(54px, 54px) scale(0, 0);
    }
    26.086957% {
      transform: translate(54px, 54px) scale(0, 0);
    }
    52.173913% {
      transform: translate(54px, 54px) scale(1, 1);
    }
    86.956522% {
      transform: translate(54px, 54px) scale(1, 1);
    }
    100% {
      transform: translate(54px, 54px) scale(0, 0);
    }
  }
`;

const StyledWhiteLoader = styled.svg`
  height: 100%;

  #egqdsoer94210_tr {
    animation: egqdsoer94210_tr__tr 2300ms linear infinite normal forwards;
  }
  @keyframes egqdsoer94210_tr__tr {
    0% {
      transform: translate(54px, 54px) rotate(-45deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    26.086957% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    39.130435% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    78.260870% {
      transform: translate(54px, 54px) rotate(180deg);
    }
    100% {
      transform: translate(54px, 54px) rotate(180deg);
    }
  }
  #egqdsoer94210 {
    animation-name: egqdsoer94210_s_o, egqdsoer94210_s_do;
    animation-duration: 2300ms;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    animation-direction: normal;
    animation-iteration-count: infinite;
  }
  @keyframes egqdsoer94210_s_o {
    0% {
      stroke-opacity: 0;
    }
    8.695652% {
      stroke-opacity: 1;
    }
    69.565217% {
      stroke-opacity: 1;
    }
    78.260870% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 0;
    }
  }
  @keyframes egqdsoer94210_s_do {
    0% {
      stroke-dashoffset: 942.48;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    26.086957% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    39.130435% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    78.260870% {
      stroke-dashoffset: 314.16;
    }
    100% {
      stroke-dashoffset: 314.16;
    }
  }
  #egqdsoer94211_tr {
    animation: egqdsoer94211_tr__tr 2300ms linear infinite normal forwards;
  }
  @keyframes egqdsoer94211_tr__tr {
    0% {
      transform: translate(54px, 54px) rotate(-45deg);
    }
    13.043478% {
      transform: translate(54px, 54px) rotate(-45deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    30.434783% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    52.173913% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    82.608696% {
      transform: translate(54px, 54px) rotate(180deg);
    }
    100% {
      transform: translate(54px, 54px) rotate(180deg);
    }
  }
  #egqdsoer94211 {
    animation-name: egqdsoer94211_s_o, egqdsoer94211_s_do;
    animation-duration: 2300ms;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    animation-direction: normal;
    animation-iteration-count: infinite;
  }
  @keyframes egqdsoer94211_s_o {
    0% {
      stroke-opacity: 0;
    }
    13.043478% {
      stroke-opacity: 0;
    }
    17.391304% {
      stroke-opacity: 1;
    }
    73.913043% {
      stroke-opacity: 1;
    }
    82.608696% {
      stroke-opacity: 0;
    }
    100% {
      stroke-opacity: 0;
    }
  }
  @keyframes egqdsoer94211_s_do {
    0% {
      stroke-dashoffset: 942.48;
    }
    17.391304% {
      stroke-dashoffset: 942.48;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    30.434783% {
      stroke-dashoffset: 800;
    }
    47.826087% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    60.869565% {
      stroke-dashoffset: 567.581767;
    }
    82.608696% {
      stroke-dashoffset: 314.16;
    }
    100% {
      stroke-dashoffset: 314.16;
    }
  }
  #egqdsoer94212_tr {
    animation: egqdsoer94212_tr__tr 2300ms linear infinite normal forwards;
  }
  @keyframes egqdsoer94212_tr__tr {
    0% {
      transform: translate(54px, 54px) rotate(-45deg);
    }
    17.391304% {
      transform: translate(54px, 54px) rotate(-45deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    39.130435% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    65.217391% {
      transform: translate(54px, 54px) rotate(0deg);
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    86.956522% {
      transform: translate(54px, 54px) rotate(180deg);
    }
    100% {
      transform: translate(54px, 54px) rotate(180deg);
    }
  }
  #egqdsoer94212 {
    animation-name: egqdsoer94212_s_o, egqdsoer94212_s_do;
    animation-duration: 2300ms;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    animation-direction: normal;
    animation-iteration-count: infinite;
  }
  @keyframes egqdsoer94212_s_o {
    0% {
      stroke-opacity: 0;
    }
    17.391304% {
      stroke-opacity: 0;
    }
    26.086957% {
      stroke-opacity: 1;
    }
    86.956522% {
      stroke-opacity: 1;
    }
    100% {
      stroke-opacity: 0;
    }
  }
  @keyframes egqdsoer94212_s_do {
    0% {
      stroke-dashoffset: 942.48;
    }
    26.086957% {
      stroke-dashoffset: 942.48;
      animation-timing-function: cubic-bezier(0.83, 0.06, 0.49, 0.99);
    }
    56.521739% {
      stroke-dashoffset: 628.32;
      animation-timing-function: cubic-bezier(0.3, 0, 0.935, 0.715);
    }
    100% {
      stroke-dashoffset: 314.16;
    }
  }
  #egqdsoer94213_ts {
    animation: egqdsoer94213_ts__ts 2300ms linear infinite normal forwards;
  }
  @keyframes egqdsoer94213_ts__ts {
    0% {
      transform: translate(54px, 54px) scale(0, 0);
    }
    26.086957% {
      transform: translate(54px, 54px) scale(0, 0);
    }
    52.173913% {
      transform: translate(54px, 54px) scale(1, 1);
    }
    86.956522% {
      transform: translate(54px, 54px) scale(1, 1);
    }
    100% {
      transform: translate(54px, 54px) scale(0, 0);
    }
  }
`;

export const useSentryMountUnmountTransaction = (options: { name: string; op?: string }) => {
  useEffect(() => {
    const span = Sentry.startInactiveSpan({
      name: options.name,
      op: options.op ?? "mount/unmount",
      forceTransaction: true,
    });

    return () => {
      span.end();
    };
  }, []);
};

export const Loader = ({
  color,
  cssOnly,
  delay,
  size,
  style,
  title,
  verticalAlign,
}: LoaderProps) => {
  const [showTitle, setShowTitle] = useState<boolean>(!Boolean(delay));

  useEffect(() => {
    setTimeout(() => {
      setShowTitle(true);
    }, delay);
  }, []);

  if (cssOnly) {
    return <CSSSpinner className={size} style={style} />;
  }

  const loaderIcon = (
    <LoaderIcon
      className={size}
      style={{ ...style, ...(verticalAlign ? { display: "flex", alignItems: "center" } : {}) }}
    >
      {color === "white" ? (
        <StyledWhiteLoader
          id="e8erxt12eadt1"
          viewBox="0 0 24 24"
          shapeRendering="geometricPrecision"
          textRendering="geometricPrecision"
        >
          <g id="egqdsoer9422" transform="matrix(0.22242055742013 0 0 0.22242055742013 0 0)">
            <g
              id="egqdsoer9423"
              display="none"
              transform="matrix(2.69355101994029 0 0 2.69355101994029 0 1)"
            >
              <g id="egqdsoer9424" transform="matrix(1 0 0 1 -760 -1190)">
                <g id="egqdsoer9425" transform="matrix(1 0 0 1 753 1185)">
                  <path
                    id="egqdsoer9426"
                    d="M34.411191,19.708523C32.866253,17.826284,30.538902,16.765485,28.108824,16.835926C25.709416,16.922023,23.487126,18.117133,22.091836,20.071759C20.708578,22.049299,20.313313,24.556175,21.020354,26.867386C21.734240,29.172728,23.477741,31.016095,25.737871,31.855118C28.298044,32.783207,31.153760,32.314174,33.279896,30.616390C33.664043,30.333991,33.861678,29.862700,33.794254,29.389827C33.726830,28.916955,33.405270,28.519110,32.957374,28.354412C32.509478,28.189714,32.007810,28.284845,31.651753,28.601998C31.468291,28.749433,31.277222,28.887124,31.079332,29.014503C31.325486,28.854102,31.004907,29.054593,30.970556,29.071772C30.870309,29.132409,30.764945,29.184144,30.655669,29.226384C30.447133,29.326366,30.233027,29.414288,30.014405,29.489715C29.980040,29.489688,29.619307,29.609851,29.899883,29.524038C29.791089,29.558366,29.676559,29.581219,29.562033,29.609807C29.344423,29.649786,29.121085,29.689760,28.897734,29.712529L28.559818,29.712266C28.198991,29.711986,28.806096,29.712457,28.439542,29.712173C28.216173,29.711999,27.987077,29.711821,27.769396,29.660034L27.460129,29.677000C27.402855,29.676955,27.013316,29.579151,27.282580,29.676862C26.852227,29.538401,26.434388,29.363470,26.033601,29.153970C25.701292,28.998856,26.239890,29.286045,25.924736,29.096532C25.827322,29.033366,25.729916,28.981672,25.632496,28.912772C25.437662,28.780706,25.248551,28.642909,25.059436,28.499377C24.967658,28.425417,24.879665,28.346864,24.795793,28.264021C25.019326,28.476405,24.732734,28.189412,24.704083,28.172183C24.537851,27.994256,24.383069,27.810603,24.233992,27.598277C24.188128,27.540888,23.884207,27.064613,24.016119,27.299867C23.872098,27.066502,23.749232,26.820713,23.648996,26.565450C23.554272,26.353008,23.473741,26.134519,23.407938,25.911426C23.494063,26.186793,23.407872,25.825395,23.407845,25.790982C23.407818,25.756570,23.356125,25.567262,23.338858,25.458275C23.321591,25.349289,23.281295,25.085429,23.263957,24.884676C23.258167,24.771948,23.258079,24.659007,23.263695,24.546287C23.263917,24.833058,23.263552,24.362754,23.263517,24.316871C23.274615,24.092156,23.297382,23.868190,23.331726,23.645881C23.331637,23.531173,23.377371,23.422235,23.400201,23.319016C23.343140,23.594271,23.400134,23.232985,23.428749,23.204330C23.480149,23.015101,23.543021,22.848823,23.605885,22.671074C23.668748,22.493326,23.691631,22.458931,23.737366,22.349994C23.783101,22.241056,23.971829,21.885608,23.834572,22.143595C24.065610,21.736061,24.335777,21.352096,24.641246,20.997139C24.675580,20.957018,24.818658,20.819479,24.641246,20.997139C24.732818,20.911179,24.812935,20.825210,24.904516,20.750721C25.111685,20.559576,25.333707,20.385226,25.568490,20.229315C25.568490,20.229315,25.929138,20.000178,25.671525,20.154834L25.980671,19.983012C26.152427,19.897114,26.329910,19.811221,26.501674,19.736794C26.673438,19.662367,26.713526,19.656663,26.828047,19.622339C26.942568,19.588016,27.217417,19.502198,26.942564,19.582280C27.200248,19.519391,27.457941,19.467972,27.727098,19.428034L28.059261,19.393879C28.110807,19.393919,28.569000,19.394275,28.288357,19.394057C28.517453,19.394235,28.740822,19.394408,28.958490,19.428990L29.290715,19.475131C29.342261,19.475171,29.794806,19.578760,29.519838,19.509721C29.776240,19.573532,30.028734,19.652245,30.276037,19.745460C30.488018,19.831656,30.694271,19.917847,30.894810,20.021240C30.636975,19.889125,30.969306,20.072917,31.003680,20.084414C31.135918,20.161943,31.264067,20.246274,31.387611,20.337070C31.609868,20.496285,31.822304,20.668829,32.023753,20.853751C31.805956,20.652842,32.081085,20.928356,32.109740,20.951319C32.224381,21.071852,32.327564,21.186640,32.430759,21.318635C32.894355,21.825165,33.670510,21.884753,34.204359,21.454799C34.738208,21.024846,34.846260,20.253132,34.451270,19.691348L34.411191,19.708523Z"
                    transform="matrix(-0.91354500000000 -0.40673700000000 0.40673700000000 -0.91354500000000 42.97500000000000 58.29110000000000)"
                    fill="rgb(195,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                  <path
                    id="egqdsoer9427"
                    d="M40.562807,18.975511C38.980857,14.814366,35.548969,11.633325,31.285975,10.376750C27.022980,9.120175,22.418897,9.932505,18.840551,12.572582C15.288517,15.211457,13.163390,19.355793,13.090471,23.786151C13.017553,28.216508,15.005139,32.428818,18.468398,35.183640C21.948888,37.902911,26.482897,38.869565,30.766607,37.805628C31.290505,37.676453,31.806631,37.517461,32.312472,37.329425C32.981248,37.075931,33.318330,36.327149,33.065365,35.656975C32.812400,34.986801,32.065181,34.649014,31.396404,34.902508C30.946129,35.072984,30.487504,35.220434,30.022302,35.344287L29.890618,35.344287C29.777298,35.371369,29.662630,35.392435,29.547092,35.407399C29.283723,35.464773,29.014628,35.504934,28.739808,35.539359C28.281774,35.596733,27.823740,35.631157,27.359981,35.642632L27.016455,35.642632C26.982103,35.642632,26.604225,35.642632,26.896221,35.642632C26.632852,35.642632,26.369483,35.608207,26.100388,35.579520C25.648188,35.532061,25.198994,35.459274,24.754913,35.361499C24.531622,35.315600,24.308330,35.263964,24.090764,35.200853C24.062137,35.200853,23.701435,35.086105,23.981981,35.166428L23.546849,35.028731C23.123168,34.873821,22.710937,34.707437,22.292981,34.512366C22.092591,34.414830,21.886476,34.317294,21.691812,34.208284C21.588754,34.156647,21.491422,34.099273,21.394090,34.041899L21.193700,33.921414C20.393929,33.416029,19.651032,32.825471,18.977961,32.160035C18.949334,32.131348,18.691690,31.861691,18.892080,32.073974L18.657337,31.821529C18.497026,31.649407,18.359615,31.471548,18.216480,31.293689C17.904581,30.898406,17.614075,30.486634,17.346215,30.060150C17.283236,29.968352,17.225982,29.859341,17.168727,29.761806C17.111473,29.664270,16.962612,29.383138,17.100022,29.658533C16.985514,29.451987,16.882456,29.228228,16.785124,29.015945C16.582187,28.576315,16.402541,28.126260,16.246934,27.667658C16.172504,27.438162,16.103799,27.208667,16.035094,26.984908C16.000741,26.864423,15.977839,26.755413,15.943487,26.640665C15.846155,26.261997,15.983565,26.898848,15.914860,26.520180C15.816339,26.021312,15.743719,25.517657,15.697294,25.011246C15.657216,24.546517,15.645765,24.093263,15.651490,23.628535C15.657216,23.163806,15.651490,23.886717,15.651490,23.502312C15.651490,23.387564,15.651490,23.272817,15.651490,23.158069C15.651490,22.894149,15.703019,22.630229,15.743097,22.366309C15.815601,21.935909,15.915038,21.510503,16.040819,21.092608L16.126700,20.759839C16.218307,20.404121,16.040819,21.000810,16.126700,20.650829C16.206856,20.398384,16.298463,20.151676,16.390070,19.904968C16.559723,19.482371,16.750820,19.068739,16.962612,18.665692C17.065670,18.470620,17.174453,18.269811,17.294687,18.091952C17.294687,18.091952,17.500802,17.747709,17.351941,17.988679C17.437822,17.862457,17.512253,17.730497,17.598134,17.610012C17.853078,17.224999,18.130281,16.855262,18.428321,16.502695C18.569947,16.326393,18.720934,16.157853,18.880629,15.997805L19.121097,15.745359L19.281409,15.584713C19.166900,15.682248,19.178351,15.682248,19.281409,15.584713C20.001549,14.935220,20.784013,14.358676,21.617381,13.863495C21.943730,13.662686,21.376914,13.983980,21.720439,13.806121C21.829222,13.743010,21.932279,13.697111,22.041062,13.645474C22.258629,13.536464,22.470469,13.444666,22.693761,13.352867C23.139374,13.165449,23.594174,13.000766,24.056411,12.859452L24.743462,12.675855C24.445740,12.738967,24.829344,12.675855,24.869422,12.675855C24.909500,12.675855,25.098439,12.629956,25.212947,12.607007C25.707191,12.522013,26.206294,12.468357,26.707282,12.446360C26.936299,12.446360,27.171042,12.446360,27.394333,12.446360L27.858093,12.446360C28.350234,12.477635,28.839832,12.540879,29.323801,12.635694C29.581445,12.681593,29.839089,12.733229,30.096733,12.802078C30.205516,12.802078,30.308574,12.859452,30.423082,12.888139C30.497154,12.905843,30.569863,12.928852,30.640648,12.956987C30.508963,12.911088,30.508963,12.911088,30.640648,12.956987C31.138760,13.129109,31.631146,13.312706,32.112082,13.530727C32.346824,13.639737,32.587292,13.754485,32.816309,13.880707C32.919367,13.932344,33.016699,13.989718,33.108306,14.047092C33.199912,14.104466,33.549163,14.311012,33.308695,14.161839C33.756783,14.442081,34.185275,14.752621,34.591190,15.091297C34.797305,15.263419,34.997695,15.435540,35.163733,15.613399L35.415651,15.848633C35.444278,15.883057,35.776353,16.227300,35.575963,16.020754C35.945747,16.411646,36.288177,16.827638,36.600814,17.265768C36.761126,17.483789,36.915712,17.707547,37.058848,17.942780C37.127553,18.034578,37.184807,18.143589,37.247786,18.241125C37.310766,18.338660,37.516881,18.711591,37.373746,18.453408C37.648095,18.932596,37.887285,19.431153,38.089424,19.945130C38.213618,20.276600,38.465529,20.544152,38.788534,20.687651C39.111540,20.831150,39.478542,20.838555,39.807051,20.708203C40.474442,20.425183,40.815056,19.677752,40.591434,18.986985L40.562807,18.975511Z"
                    fill="rgb(190,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                  <path
                    id="egqdsoer9428"
                    d="M39.956106,10.304285C35.131842,6.515340,28.803774,5.209674,22.862642,6.777403C16.921510,8.345132,12.055305,12.604710,9.717984,18.283434C7.463048,23.877233,7.879358,30.187842,10.849014,35.428070C13.885071,40.729012,19.231102,44.301345,25.297616,45.082935C31.364130,45.864526,37.452582,43.765377,41.752742,39.409611C42.280727,38.891241,42.777178,38.341763,43.239420,37.764135C43.642117,37.200702,43.530927,36.421366,42.986933,35.994443C42.442939,35.567521,41.658321,35.643833,41.205247,36.167732C40.758858,36.735843,40.277674,37.275810,39.764469,37.784522L39.603758,37.950800L39.603758,37.950800L39.207764,38.312066C38.926566,38.552926,38.633904,38.788068,38.352711,39.023199C37.813314,39.436155,37.268204,39.826201,36.694465,40.170446L36.252688,40.434368L36.046145,40.554859C36.264156,40.434357,36.086284,40.554822,36.046145,40.554859C35.742083,40.715539,35.432298,40.864768,35.122513,41.013997C34.502959,41.295269,33.866192,41.588014,33.223785,41.777650C32.896840,41.881065,32.575628,41.984476,32.248694,42.076435C32.179883,42.076498,31.784111,42.197163,32.025001,42.139656L31.451458,42.271942C30.791896,42.410035,30.120903,42.508040,29.444197,42.583135C29.146017,42.583410,28.870728,42.635221,28.549601,42.646974L28.039256,42.647444L27.695203,42.647761C26.351877,42.628646,25.015229,42.458858,23.710392,42.141588C23.567069,42.107348,23.412277,42.073119,23.274698,42.027417L22.993800,41.941747C22.649848,41.833221,22.300166,41.718971,21.961964,41.593254C21.326002,41.356123,20.704053,41.083196,20.099099,40.775782C19.801057,40.627113,19.525962,40.466966,19.216463,40.306850C19.457178,40.438386,19.027345,40.186724,18.958571,40.146687C18.792379,40.037997,18.620448,39.935041,18.459995,39.820617C17.874887,39.425172,17.312449,38.997286,16.775319,38.538964C16.500293,38.304345,16.236736,38.069716,15.978934,37.812167L15.635188,37.480225C15.451867,37.291351,15.595090,37.434433,15.635188,37.480225L15.388854,37.222665C14.918243,36.705490,14.476540,36.162768,14.065751,35.596964C13.673087,35.044011,13.309944,34.470689,12.977835,33.879389C12.880517,33.701892,13.075158,34.051157,12.977835,33.879389L12.851851,33.696190C12.777427,33.564501,12.714483,33.421344,12.640060,33.289655C12.491256,32.980448,12.348180,32.676964,12.216584,32.362013C11.952279,31.736467,11.725020,31.095879,11.535982,30.443561L11.415965,30.008299C11.415965,30.008299,11.324535,29.664668,11.370245,29.842212C11.415954,30.019756,11.370318,29.762011,11.370350,29.727640C11.290388,29.383998,11.216171,29.028894,11.153417,28.679507C11.043012,28.031050,10.967052,27.377145,10.925855,26.720538C10.926151,26.399737,10.892042,26.078967,10.892332,25.763894C10.892401,25.689422,10.892475,25.609222,10.892538,25.540479C10.892306,25.792537,10.892538,25.540479,10.892596,25.477464C10.892771,25.288420,10.892940,25.105105,10.893125,24.904604C10.933872,24.245779,11.003295,23.581198,11.107108,22.933771C11.164762,22.595731,11.233885,22.257680,11.302996,21.931087C11.337539,21.782112,11.372082,21.633136,11.412353,21.489884C11.452625,21.346633,11.452630,21.340904,11.464167,21.266422C11.556253,20.899707,11.389390,21.518549,11.498678,21.151818C11.891804,19.835022,12.423489,18.563318,13.084820,17.358028C13.130757,17.289243,13.165231,17.214739,13.205428,17.151688C13.314563,16.951086,13.038893,17.415356,13.239892,17.088641L13.492567,16.687407C13.687805,16.389340,13.888783,16.085540,14.101223,15.787457C14.528780,15.200759,14.990756,14.639856,15.484718,14.107705C15.588045,13.987309,15.702835,13.872631,15.811890,13.757959L16.018512,13.551539C16.047183,13.551513,16.225129,13.350848,16.110339,13.465526L16.242342,13.339375C16.294003,13.282041,16.357122,13.236154,16.414517,13.178816L16.850676,12.788870C17.369994,12.348958,17.914105,11.939144,18.480325,11.561449C18.750002,11.377886,19.054079,11.200019,19.306538,11.033657L19.507341,10.918900L19.667978,10.832823C19.530283,10.913151,19.742560,10.792655,19.776981,10.775437L20.075303,10.620490C20.660115,10.320536,21.261518,10.054125,21.876582,9.822556C22.513673,9.584063,23.162707,9.378868,23.821046,9.207805L24.050462,9.156036C24.211057,9.115788,24.377376,9.086992,24.549434,9.052462C24.899285,8.983396,25.254859,8.925783,25.610428,8.873898C26.262683,8.784772,26.919389,8.732528,27.577407,8.717414L28.087752,8.716943C27.823978,8.717186,28.259779,8.716785,28.322855,8.716727C28.661174,8.716415,29.005227,8.716098,29.337753,8.778806C30.017252,8.838615,30.692898,8.935524,31.361662,9.069099C31.676986,9.131823,31.992299,9.206005,32.307607,9.285914C32.370684,9.285856,32.617171,9.377287,32.370684,9.285856L32.594255,9.354393L33.167523,9.519994C33.827922,9.736973,34.474865,9.992699,35.104980,10.285841C35.385835,10.417340,35.678154,10.554556,35.941795,10.697528L36.400300,10.949164C36.434706,10.949132,36.652474,11.092146,36.503458,11.012083C36.354442,10.932020,36.681124,11.115034,36.709768,11.143651C37.305488,11.508712,37.879501,11.907943,38.428929,12.339343C38.991190,12.774232,39.799197,12.679281,40.246871,12.125710C40.636980,11.529674,40.519435,10.737124,39.973330,10.281355L39.956106,10.304285Z"
                    transform="matrix(0.51503800000000 -0.85716700000000 0.85716700000000 0.51503800000000 -9.46547000000000 34.64120000000000)"
                    fill="rgb(156,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                  <path
                    id="egqdsoer9429"
                    d="M27.050808,23.835081C27.136689,23.743283,27.193943,23.754757,27.050808,23.806394C27.085160,23.806394,27.354255,23.703121,27.228296,23.720333C27.102337,23.737545,27.342804,23.720333,27.365706,23.720333C27.388608,23.720333,27.451587,23.720333,27.331354,23.720333L27.445862,23.777707C27.543194,23.863768,27.526018,23.915404,27.445862,23.777707C27.445862,23.823606,27.543194,24.099001,27.531743,23.955566C27.520293,23.812131,27.503116,24.076051,27.531743,24.093263C27.560371,24.110476,27.503116,24.173587,27.531743,24.093263C27.560371,24.012940,27.531743,24.030152,27.491665,24.162112C27.451587,24.294072,27.348530,24.248173,27.491665,24.162112L27.256923,24.253910C27.451587,24.185062,27.256923,24.219486,27.165316,24.225223C27.073709,24.230961,27.090886,24.225223,27.165316,24.225223C27.239747,24.225223,27.234021,24.253910,27.165316,24.225223C27.096611,24.196537,26.993554,24.012940,27.079435,24.179324C27.042058,24.103764,27.011403,24.025049,26.987828,23.944091C27.039357,24.116213,27.022181,23.944091,26.987828,23.852293C26.953476,23.760495,26.987828,23.777707,26.987828,23.852293C26.987828,23.926879,26.987828,23.938354,27.033632,23.806394C26.592524,24.370418,26.691213,25.185986,27.254060,25.628016C27.816908,26.070045,28.630774,25.971150,29.071882,25.407126C29.845737,24.414900,29.677141,22.983122,28.694076,22.198643C27.711010,21.414165,26.280742,21.570063,25.488977,22.547997C24.697212,23.525931,24.839750,24.960549,25.808391,25.762844C26.810032,26.559313,28.264550,26.400772,29.071882,25.407126C29.483532,24.840291,29.373575,24.048631,28.823105,23.615987C28.272635,23.183343,27.479180,23.264964,27.027906,23.800656L27.050808,23.835081Z"
                    fill="rgb(173,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                </g>
              </g>
            </g>
            <g id="egqdsoer94210_tr" transform="translate(54,54) rotate(-45)">
              <circle
                id="egqdsoer94210"
                r="50"
                transform="translate(0,-0)"
                fill="none"
                stroke="rgb(255,255,255)"
                strokeWidth="8"
                strokeOpacity="0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDashoffset="942.480000"
                strokeDasharray="314.160000"
              />
            </g>
            <g id="egqdsoer94211_tr" transform="translate(54,54) rotate(-45)">
              <circle
                id="egqdsoer94211"
                r="46.666667"
                transform="scale(0.750000,0.750000) translate(0,0)"
                fill="none"
                stroke="rgb(255,255,255)"
                strokeWidth="10"
                strokeOpacity="0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDashoffset="942.480000"
                strokeDasharray="314.160000"
              />
            </g>
            <g id="egqdsoer94212_tr" transform="translate(54,54) rotate(-45)">
              <circle
                id="egqdsoer94212"
                r="50"
                transform="scale(0.360000,0.360000) translate(0,-0)"
                fill="none"
                stroke="rgb(255,255,255)"
                strokeWidth="18"
                strokeOpacity="0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDashoffset="942.480000"
                strokeDasharray="314.160000"
              />
            </g>
            <g id="egqdsoer94213_ts" transform="translate(54,54) scale(0,0)">
              <circle
                id="egqdsoer94213"
                r="6.820000"
                transform="translate(0,0)"
                fill="rgb(255,255,255)"
                stroke="none"
                strokeWidth="1"
              />
            </g>
          </g>
        </StyledWhiteLoader>
      ) : (
        <StyledBlueLoader
          id="e8erxt12eadt1"
          viewBox="0 0 64 64"
          shapeRendering="geometricPrecision"
          textRendering="geometricPrecision"
        >
          <g id="e8erxt12eadt2" transform="matrix(0.59177855773474 0 0 0.59177855773474 0 0)">
            <g
              id="e8erxt12eadt3"
              display="none"
              transform="matrix(2.69355101994029 0 0 2.69355101994029 0 1)"
            >
              <g id="e8erxt12eadt4" transform="matrix(1 0 0 1 -760 -1190)">
                <g id="e8erxt12eadt5" transform="matrix(1 0 0 1 753 1185)">
                  <path
                    id="e8erxt12eadt6"
                    d="M34.411191,19.708523C32.866253,17.826284,30.538902,16.765485,28.108824,16.835926C25.709416,16.922023,23.487126,18.117133,22.091836,20.071759C20.708578,22.049299,20.313313,24.556175,21.020354,26.867386C21.734240,29.172728,23.477741,31.016095,25.737871,31.855118C28.298044,32.783207,31.153760,32.314174,33.279896,30.616390C33.664043,30.333991,33.861678,29.862700,33.794254,29.389827C33.726830,28.916955,33.405270,28.519110,32.957374,28.354412C32.509478,28.189714,32.007810,28.284845,31.651753,28.601998C31.468291,28.749433,31.277222,28.887124,31.079332,29.014503C31.325486,28.854102,31.004907,29.054593,30.970556,29.071772C30.870309,29.132409,30.764945,29.184144,30.655669,29.226384C30.447133,29.326366,30.233027,29.414288,30.014405,29.489715C29.980040,29.489688,29.619307,29.609851,29.899883,29.524038C29.791089,29.558366,29.676559,29.581219,29.562033,29.609807C29.344423,29.649786,29.121085,29.689760,28.897734,29.712529L28.559818,29.712266C28.198991,29.711986,28.806096,29.712457,28.439542,29.712173C28.216173,29.711999,27.987077,29.711821,27.769396,29.660034L27.460129,29.677000C27.402855,29.676955,27.013316,29.579151,27.282580,29.676862C26.852227,29.538401,26.434388,29.363470,26.033601,29.153970C25.701292,28.998856,26.239890,29.286045,25.924736,29.096532C25.827322,29.033366,25.729916,28.981672,25.632496,28.912772C25.437662,28.780706,25.248551,28.642909,25.059436,28.499377C24.967658,28.425417,24.879665,28.346864,24.795793,28.264021C25.019326,28.476405,24.732734,28.189412,24.704083,28.172183C24.537851,27.994256,24.383069,27.810603,24.233992,27.598277C24.188128,27.540888,23.884207,27.064613,24.016119,27.299867C23.872098,27.066502,23.749232,26.820713,23.648996,26.565450C23.554272,26.353008,23.473741,26.134519,23.407938,25.911426C23.494063,26.186793,23.407872,25.825395,23.407845,25.790982C23.407818,25.756570,23.356125,25.567262,23.338858,25.458275C23.321591,25.349289,23.281295,25.085429,23.263957,24.884676C23.258167,24.771948,23.258079,24.659007,23.263695,24.546287C23.263917,24.833058,23.263552,24.362754,23.263517,24.316871C23.274615,24.092156,23.297382,23.868190,23.331726,23.645881C23.331637,23.531173,23.377371,23.422235,23.400201,23.319016C23.343140,23.594271,23.400134,23.232985,23.428749,23.204330C23.480149,23.015101,23.543021,22.848823,23.605885,22.671074C23.668748,22.493326,23.691631,22.458931,23.737366,22.349994C23.783101,22.241056,23.971829,21.885608,23.834572,22.143595C24.065610,21.736061,24.335777,21.352096,24.641246,20.997139C24.675580,20.957018,24.818658,20.819479,24.641246,20.997139C24.732818,20.911179,24.812935,20.825210,24.904516,20.750721C25.111685,20.559576,25.333707,20.385226,25.568490,20.229315C25.568490,20.229315,25.929138,20.000178,25.671525,20.154834L25.980671,19.983012C26.152427,19.897114,26.329910,19.811221,26.501674,19.736794C26.673438,19.662367,26.713526,19.656663,26.828047,19.622339C26.942568,19.588016,27.217417,19.502198,26.942564,19.582280C27.200248,19.519391,27.457941,19.467972,27.727098,19.428034L28.059261,19.393879C28.110807,19.393919,28.569000,19.394275,28.288357,19.394057C28.517453,19.394235,28.740822,19.394408,28.958490,19.428990L29.290715,19.475131C29.342261,19.475171,29.794806,19.578760,29.519838,19.509721C29.776240,19.573532,30.028734,19.652245,30.276037,19.745460C30.488018,19.831656,30.694271,19.917847,30.894810,20.021240C30.636975,19.889125,30.969306,20.072917,31.003680,20.084414C31.135918,20.161943,31.264067,20.246274,31.387611,20.337070C31.609868,20.496285,31.822304,20.668829,32.023753,20.853751C31.805956,20.652842,32.081085,20.928356,32.109740,20.951319C32.224381,21.071852,32.327564,21.186640,32.430759,21.318635C32.894355,21.825165,33.670510,21.884753,34.204359,21.454799C34.738208,21.024846,34.846260,20.253132,34.451270,19.691348L34.411191,19.708523Z"
                    transform="matrix(-0.91354500000000 -0.40673700000000 0.40673700000000 -0.91354500000000 42.97500000000000 58.29110000000000)"
                    fill="rgb(195,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                  <path
                    id="e8erxt12eadt7"
                    d="M40.562807,18.975511C38.980857,14.814366,35.548969,11.633325,31.285975,10.376750C27.022980,9.120175,22.418897,9.932505,18.840551,12.572582C15.288517,15.211457,13.163390,19.355793,13.090471,23.786151C13.017553,28.216508,15.005139,32.428818,18.468398,35.183640C21.948888,37.902911,26.482897,38.869565,30.766607,37.805628C31.290505,37.676453,31.806631,37.517461,32.312472,37.329425C32.981248,37.075931,33.318330,36.327149,33.065365,35.656975C32.812400,34.986801,32.065181,34.649014,31.396404,34.902508C30.946129,35.072984,30.487504,35.220434,30.022302,35.344287L29.890618,35.344287C29.777298,35.371369,29.662630,35.392435,29.547092,35.407399C29.283723,35.464773,29.014628,35.504934,28.739808,35.539359C28.281774,35.596733,27.823740,35.631157,27.359981,35.642632L27.016455,35.642632C26.982103,35.642632,26.604225,35.642632,26.896221,35.642632C26.632852,35.642632,26.369483,35.608207,26.100388,35.579520C25.648188,35.532061,25.198994,35.459274,24.754913,35.361499C24.531622,35.315600,24.308330,35.263964,24.090764,35.200853C24.062137,35.200853,23.701435,35.086105,23.981981,35.166428L23.546849,35.028731C23.123168,34.873821,22.710937,34.707437,22.292981,34.512366C22.092591,34.414830,21.886476,34.317294,21.691812,34.208284C21.588754,34.156647,21.491422,34.099273,21.394090,34.041899L21.193700,33.921414C20.393929,33.416029,19.651032,32.825471,18.977961,32.160035C18.949334,32.131348,18.691690,31.861691,18.892080,32.073974L18.657337,31.821529C18.497026,31.649407,18.359615,31.471548,18.216480,31.293689C17.904581,30.898406,17.614075,30.486634,17.346215,30.060150C17.283236,29.968352,17.225982,29.859341,17.168727,29.761806C17.111473,29.664270,16.962612,29.383138,17.100022,29.658533C16.985514,29.451987,16.882456,29.228228,16.785124,29.015945C16.582187,28.576315,16.402541,28.126260,16.246934,27.667658C16.172504,27.438162,16.103799,27.208667,16.035094,26.984908C16.000741,26.864423,15.977839,26.755413,15.943487,26.640665C15.846155,26.261997,15.983565,26.898848,15.914860,26.520180C15.816339,26.021312,15.743719,25.517657,15.697294,25.011246C15.657216,24.546517,15.645765,24.093263,15.651490,23.628535C15.657216,23.163806,15.651490,23.886717,15.651490,23.502312C15.651490,23.387564,15.651490,23.272817,15.651490,23.158069C15.651490,22.894149,15.703019,22.630229,15.743097,22.366309C15.815601,21.935909,15.915038,21.510503,16.040819,21.092608L16.126700,20.759839C16.218307,20.404121,16.040819,21.000810,16.126700,20.650829C16.206856,20.398384,16.298463,20.151676,16.390070,19.904968C16.559723,19.482371,16.750820,19.068739,16.962612,18.665692C17.065670,18.470620,17.174453,18.269811,17.294687,18.091952C17.294687,18.091952,17.500802,17.747709,17.351941,17.988679C17.437822,17.862457,17.512253,17.730497,17.598134,17.610012C17.853078,17.224999,18.130281,16.855262,18.428321,16.502695C18.569947,16.326393,18.720934,16.157853,18.880629,15.997805L19.121097,15.745359L19.281409,15.584713C19.166900,15.682248,19.178351,15.682248,19.281409,15.584713C20.001549,14.935220,20.784013,14.358676,21.617381,13.863495C21.943730,13.662686,21.376914,13.983980,21.720439,13.806121C21.829222,13.743010,21.932279,13.697111,22.041062,13.645474C22.258629,13.536464,22.470469,13.444666,22.693761,13.352867C23.139374,13.165449,23.594174,13.000766,24.056411,12.859452L24.743462,12.675855C24.445740,12.738967,24.829344,12.675855,24.869422,12.675855C24.909500,12.675855,25.098439,12.629956,25.212947,12.607007C25.707191,12.522013,26.206294,12.468357,26.707282,12.446360C26.936299,12.446360,27.171042,12.446360,27.394333,12.446360L27.858093,12.446360C28.350234,12.477635,28.839832,12.540879,29.323801,12.635694C29.581445,12.681593,29.839089,12.733229,30.096733,12.802078C30.205516,12.802078,30.308574,12.859452,30.423082,12.888139C30.497154,12.905843,30.569863,12.928852,30.640648,12.956987C30.508963,12.911088,30.508963,12.911088,30.640648,12.956987C31.138760,13.129109,31.631146,13.312706,32.112082,13.530727C32.346824,13.639737,32.587292,13.754485,32.816309,13.880707C32.919367,13.932344,33.016699,13.989718,33.108306,14.047092C33.199912,14.104466,33.549163,14.311012,33.308695,14.161839C33.756783,14.442081,34.185275,14.752621,34.591190,15.091297C34.797305,15.263419,34.997695,15.435540,35.163733,15.613399L35.415651,15.848633C35.444278,15.883057,35.776353,16.227300,35.575963,16.020754C35.945747,16.411646,36.288177,16.827638,36.600814,17.265768C36.761126,17.483789,36.915712,17.707547,37.058848,17.942780C37.127553,18.034578,37.184807,18.143589,37.247786,18.241125C37.310766,18.338660,37.516881,18.711591,37.373746,18.453408C37.648095,18.932596,37.887285,19.431153,38.089424,19.945130C38.213618,20.276600,38.465529,20.544152,38.788534,20.687651C39.111540,20.831150,39.478542,20.838555,39.807051,20.708203C40.474442,20.425183,40.815056,19.677752,40.591434,18.986985L40.562807,18.975511Z"
                    fill="rgb(190,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                  <path
                    id="e8erxt12eadt8"
                    d="M39.956106,10.304285C35.131842,6.515340,28.803774,5.209674,22.862642,6.777403C16.921510,8.345132,12.055305,12.604710,9.717984,18.283434C7.463048,23.877233,7.879358,30.187842,10.849014,35.428070C13.885071,40.729012,19.231102,44.301345,25.297616,45.082935C31.364130,45.864526,37.452582,43.765377,41.752742,39.409611C42.280727,38.891241,42.777178,38.341763,43.239420,37.764135C43.642117,37.200702,43.530927,36.421366,42.986933,35.994443C42.442939,35.567521,41.658321,35.643833,41.205247,36.167732C40.758858,36.735843,40.277674,37.275810,39.764469,37.784522L39.603758,37.950800L39.603758,37.950800L39.207764,38.312066C38.926566,38.552926,38.633904,38.788068,38.352711,39.023199C37.813314,39.436155,37.268204,39.826201,36.694465,40.170446L36.252688,40.434368L36.046145,40.554859C36.264156,40.434357,36.086284,40.554822,36.046145,40.554859C35.742083,40.715539,35.432298,40.864768,35.122513,41.013997C34.502959,41.295269,33.866192,41.588014,33.223785,41.777650C32.896840,41.881065,32.575628,41.984476,32.248694,42.076435C32.179883,42.076498,31.784111,42.197163,32.025001,42.139656L31.451458,42.271942C30.791896,42.410035,30.120903,42.508040,29.444197,42.583135C29.146017,42.583410,28.870728,42.635221,28.549601,42.646974L28.039256,42.647444L27.695203,42.647761C26.351877,42.628646,25.015229,42.458858,23.710392,42.141588C23.567069,42.107348,23.412277,42.073119,23.274698,42.027417L22.993800,41.941747C22.649848,41.833221,22.300166,41.718971,21.961964,41.593254C21.326002,41.356123,20.704053,41.083196,20.099099,40.775782C19.801057,40.627113,19.525962,40.466966,19.216463,40.306850C19.457178,40.438386,19.027345,40.186724,18.958571,40.146687C18.792379,40.037997,18.620448,39.935041,18.459995,39.820617C17.874887,39.425172,17.312449,38.997286,16.775319,38.538964C16.500293,38.304345,16.236736,38.069716,15.978934,37.812167L15.635188,37.480225C15.451867,37.291351,15.595090,37.434433,15.635188,37.480225L15.388854,37.222665C14.918243,36.705490,14.476540,36.162768,14.065751,35.596964C13.673087,35.044011,13.309944,34.470689,12.977835,33.879389C12.880517,33.701892,13.075158,34.051157,12.977835,33.879389L12.851851,33.696190C12.777427,33.564501,12.714483,33.421344,12.640060,33.289655C12.491256,32.980448,12.348180,32.676964,12.216584,32.362013C11.952279,31.736467,11.725020,31.095879,11.535982,30.443561L11.415965,30.008299C11.415965,30.008299,11.324535,29.664668,11.370245,29.842212C11.415954,30.019756,11.370318,29.762011,11.370350,29.727640C11.290388,29.383998,11.216171,29.028894,11.153417,28.679507C11.043012,28.031050,10.967052,27.377145,10.925855,26.720538C10.926151,26.399737,10.892042,26.078967,10.892332,25.763894C10.892401,25.689422,10.892475,25.609222,10.892538,25.540479C10.892306,25.792537,10.892538,25.540479,10.892596,25.477464C10.892771,25.288420,10.892940,25.105105,10.893125,24.904604C10.933872,24.245779,11.003295,23.581198,11.107108,22.933771C11.164762,22.595731,11.233885,22.257680,11.302996,21.931087C11.337539,21.782112,11.372082,21.633136,11.412353,21.489884C11.452625,21.346633,11.452630,21.340904,11.464167,21.266422C11.556253,20.899707,11.389390,21.518549,11.498678,21.151818C11.891804,19.835022,12.423489,18.563318,13.084820,17.358028C13.130757,17.289243,13.165231,17.214739,13.205428,17.151688C13.314563,16.951086,13.038893,17.415356,13.239892,17.088641L13.492567,16.687407C13.687805,16.389340,13.888783,16.085540,14.101223,15.787457C14.528780,15.200759,14.990756,14.639856,15.484718,14.107705C15.588045,13.987309,15.702835,13.872631,15.811890,13.757959L16.018512,13.551539C16.047183,13.551513,16.225129,13.350848,16.110339,13.465526L16.242342,13.339375C16.294003,13.282041,16.357122,13.236154,16.414517,13.178816L16.850676,12.788870C17.369994,12.348958,17.914105,11.939144,18.480325,11.561449C18.750002,11.377886,19.054079,11.200019,19.306538,11.033657L19.507341,10.918900L19.667978,10.832823C19.530283,10.913151,19.742560,10.792655,19.776981,10.775437L20.075303,10.620490C20.660115,10.320536,21.261518,10.054125,21.876582,9.822556C22.513673,9.584063,23.162707,9.378868,23.821046,9.207805L24.050462,9.156036C24.211057,9.115788,24.377376,9.086992,24.549434,9.052462C24.899285,8.983396,25.254859,8.925783,25.610428,8.873898C26.262683,8.784772,26.919389,8.732528,27.577407,8.717414L28.087752,8.716943C27.823978,8.717186,28.259779,8.716785,28.322855,8.716727C28.661174,8.716415,29.005227,8.716098,29.337753,8.778806C30.017252,8.838615,30.692898,8.935524,31.361662,9.069099C31.676986,9.131823,31.992299,9.206005,32.307607,9.285914C32.370684,9.285856,32.617171,9.377287,32.370684,9.285856L32.594255,9.354393L33.167523,9.519994C33.827922,9.736973,34.474865,9.992699,35.104980,10.285841C35.385835,10.417340,35.678154,10.554556,35.941795,10.697528L36.400300,10.949164C36.434706,10.949132,36.652474,11.092146,36.503458,11.012083C36.354442,10.932020,36.681124,11.115034,36.709768,11.143651C37.305488,11.508712,37.879501,11.907943,38.428929,12.339343C38.991190,12.774232,39.799197,12.679281,40.246871,12.125710C40.636980,11.529674,40.519435,10.737124,39.973330,10.281355L39.956106,10.304285Z"
                    transform="matrix(0.51503800000000 -0.85716700000000 0.85716700000000 0.51503800000000 -9.46547000000000 34.64120000000000)"
                    fill="rgb(156,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                  <path
                    id="e8erxt12eadt9"
                    d="M27.050808,23.835081C27.136689,23.743283,27.193943,23.754757,27.050808,23.806394C27.085160,23.806394,27.354255,23.703121,27.228296,23.720333C27.102337,23.737545,27.342804,23.720333,27.365706,23.720333C27.388608,23.720333,27.451587,23.720333,27.331354,23.720333L27.445862,23.777707C27.543194,23.863768,27.526018,23.915404,27.445862,23.777707C27.445862,23.823606,27.543194,24.099001,27.531743,23.955566C27.520293,23.812131,27.503116,24.076051,27.531743,24.093263C27.560371,24.110476,27.503116,24.173587,27.531743,24.093263C27.560371,24.012940,27.531743,24.030152,27.491665,24.162112C27.451587,24.294072,27.348530,24.248173,27.491665,24.162112L27.256923,24.253910C27.451587,24.185062,27.256923,24.219486,27.165316,24.225223C27.073709,24.230961,27.090886,24.225223,27.165316,24.225223C27.239747,24.225223,27.234021,24.253910,27.165316,24.225223C27.096611,24.196537,26.993554,24.012940,27.079435,24.179324C27.042058,24.103764,27.011403,24.025049,26.987828,23.944091C27.039357,24.116213,27.022181,23.944091,26.987828,23.852293C26.953476,23.760495,26.987828,23.777707,26.987828,23.852293C26.987828,23.926879,26.987828,23.938354,27.033632,23.806394C26.592524,24.370418,26.691213,25.185986,27.254060,25.628016C27.816908,26.070045,28.630774,25.971150,29.071882,25.407126C29.845737,24.414900,29.677141,22.983122,28.694076,22.198643C27.711010,21.414165,26.280742,21.570063,25.488977,22.547997C24.697212,23.525931,24.839750,24.960549,25.808391,25.762844C26.810032,26.559313,28.264550,26.400772,29.071882,25.407126C29.483532,24.840291,29.373575,24.048631,28.823105,23.615987C28.272635,23.183343,27.479180,23.264964,27.027906,23.800656L27.050808,23.835081Z"
                    fill="rgb(173,39,242)"
                    stroke="none"
                    strokeWidth="1"
                  />
                </g>
              </g>
            </g>
            <g id="e8erxt12eadt10_tr" transform="translate(54,54) rotate(-45)">
              <circle
                id="e8erxt12eadt10"
                r="50"
                transform="translate(0,-0)"
                fill="none"
                stroke={COLORS.coeblue4}
                strokeWidth="8"
                strokeOpacity="0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDashoffset="942.480000"
                strokeDasharray="314.160000"
              />
            </g>
            <g id="e8erxt12eadt11_tr" transform="translate(54,54) rotate(-45)">
              <circle
                id="e8erxt12eadt11"
                r="46.666667"
                transform="scale(0.750000,0.750000) translate(0,0)"
                fill="none"
                stroke={COLORS.coeblue4}
                strokeWidth="10"
                strokeOpacity="0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDashoffset="942.480000"
                strokeDasharray="314.160000"
              />
            </g>
            <g id="e8erxt12eadt12_tr" transform="translate(54,54) rotate(-45)">
              <circle
                id="e8erxt12eadt12"
                r="50"
                transform="scale(0.360000,0.360000) translate(0,-0)"
                fill="none"
                stroke={COLORS.coeblue4}
                strokeWidth="18"
                strokeOpacity="0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDashoffset="942.480000"
                strokeDasharray="314.160000"
              />
            </g>
            <g id="e8erxt12eadt13_ts" transform="translate(54,54) scale(0,0)">
              <circle
                id="e8erxt12eadt13"
                r="6.820000"
                transform="translate(0,0)"
                fill={COLORS.coeblue4}
                stroke="none"
                strokeWidth="1"
              />
            </g>
          </g>
        </StyledBlueLoader>
      )}
    </LoaderIcon>
  );
  if (title) {
    return (
      <LoaderPanel>
        {loaderIcon}
        {showTitle && <div className="loadTitle">{title}...</div>}
      </LoaderPanel>
    );
  } else {
    return loaderIcon;
  }
};

type LoaderWithPerfTimingsProps = LoaderProps & {
  name: string;
  onScreenTimeOptions?: {
    dataSourceType?: string;
    enable?: boolean;
  };
};

export const LoaderWithPerfTimings: React.FC<LoaderWithPerfTimingsProps> = props => {
  const { name, onScreenTimeOptions } = props;
  useCaptureLoadingTime({ name: `Loader.${name}`, ...onScreenTimeOptions });
  useSentryMountUnmountTransaction({ name: `Loader.${name}` });

  return <Loader {...props} />;
};

export const FullScreenLoaderBackground = styled.div`
  height: 100vh;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
